import './ProgressBar.css';

export type ProgressBarProps = {
    /** 0–100. Ignored when indeterminate=true. */
    value?: number | undefined;
    /** If true, renders the numeric percentage label on the right */
    showLabel?: boolean | undefined;
    /** Optional aria-label for screen readers */
    ariaLabel?: string | undefined;
    /** Indeterminate mode — shows a sliding animation with no specific value */
    indeterminate?: boolean | undefined;
};

function clampPercent(v: number | undefined): number {
    if (v === undefined || !Number.isFinite(v)) return 0;
    return Math.max(0, Math.min(100, v));
}

/** Floor the rendered width so any non-zero value stays visible. A 1%
 *  fill on a 400-px track is ~4 px — technically correct, visually a
 *  dead bar. `max(N%, 0.375rem)` guarantees a ~6 px minimum slice so
 *  the user can always *see* "something has started". 0% still collapses
 *  to nothing, which is the right signal for "not yet begun". */
function renderedFillWidth(pct: number): string {
    if (pct <= 0) return '0%';
    return `max(${pct}%, 0.375rem)`;
}

function ProgressBar({ value, showLabel = false, ariaLabel, indeterminate = false }: ProgressBarProps) {
    const pct = clampPercent(value);
    const ariaProps = indeterminate
        ? { 'aria-busy': 'true' as const }
        : { 'aria-valuenow': pct, 'aria-valuemin': 0, 'aria-valuemax': 100 };

    return (
        <div
            className={`ui-progress ${indeterminate ? 'ui-progress--indeterminate' : ''}`}
            role="progressbar"
            aria-label={ariaLabel}
            {...ariaProps}
        >
            <div className="ui-progress__track">
                <div
                    className="ui-progress__fill"
                    style={indeterminate ? undefined : { width: renderedFillWidth(pct) }}
                />
            </div>
            {showLabel && !indeterminate && (
                <span className="ui-progress__label">{Math.round(pct)}%</span>
            )}
        </div>
    );
}

export default ProgressBar;
